import React, { useState } from 'react';
import { FlaskConical, ImageIcon, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useEncounter } from '@/contexts/EncounterContext';
import LabOrderSheet from '@/components/labs/LabOrderSheet';
import LinkedLabOrdersList from './LinkedLabOrdersList';
import { CreateLabOrderRequest } from '@/services/labService';

interface VisitOrdersPanelProps {
  patientDetails: CreateLabOrderRequest['patientDetails'];
  patientAddress: CreateLabOrderRequest['patientAddress'];
}

const VisitOrdersPanel: React.FC<VisitOrdersPanelProps> = ({
  patientDetails, patientAddress,
}) => {
  const {
    encounterId, appointmentId, patientId, clinicId, isSigned, encounter,
  } = useEncounter();
  const [showLab, setShowLab] = useState(false);

  // LabOrderSheet wants an address shape with `street` rather than the
  // FHIR-style `firstLine` we already have on the encounter context.
  // Map once here so we don't reshape inside the sheet.
  const sheetAddress = {
    street: patientAddress?.firstLine || '',
    city: patientAddress?.city || '',
    state: patientAddress?.state || '',
    zip: patientAddress?.zip || '',
  };

  const patientFullName = [patientDetails?.firstName, patientDetails?.lastName]
    .filter(Boolean)
    .join(' ')
    || (encounter as any)?.patient?.full_name
    || 'patient';

  return (
    <div className='space-y-4'>
      <Card>
        <CardHeader>
          <CardTitle className='text-base'>Place order</CardTitle>
        </CardHeader>
        <CardContent className='grid gap-3 sm:grid-cols-3'>
          <Button
            variant='outline'
            className='h-auto flex-col items-start py-3'
            onClick={() => setShowLab(true)}
            disabled={isSigned}
          >
            <div className='flex items-center gap-2 text-purple-700'>
              <FlaskConical className='h-4 w-4' />
              <span className='font-medium'>Lab order</span>
            </div>
            <span className='mt-1 text-xs text-slate-500'>Quest, Labcorp, in-house panels</span>
          </Button>

          <Button
            variant='outline'
            className='h-auto flex-col items-start py-3'
            disabled
            title='Coming soon'
          >
            <div className='flex items-center gap-2 text-blue-700'>
              <ImageIcon className='h-4 w-4' />
              <span className='font-medium'>Imaging</span>
            </div>
            <span className='mt-1 text-xs text-slate-500'>X-ray, ultrasound, CT, MRI (coming soon)</span>
          </Button>

          <Button
            variant='outline'
            className='h-auto flex-col items-start py-3'
            disabled
            title='Coming soon'
          >
            <div className='flex items-center gap-2 text-emerald-700'>
              <Send className='h-4 w-4' />
              <span className='font-medium'>Referral</span>
            </div>
            <span className='mt-1 text-xs text-slate-500'>Specialist referral (coming soon)</span>
          </Button>
        </CardContent>
      </Card>

      <LinkedLabOrdersList
        clinicId={clinicId}
        encounterId={encounterId}
        emptyMessage='No lab orders for this visit yet. Use "Lab order" above to place one.'
      />

      <LabOrderSheet
        open={showLab}
        onOpenChange={setShowLab}
        patientId={patientId}
        patientName={patientFullName}
        patientEmail={patientDetails?.email}
        patientPhone={patientDetails?.phone}
        patientAddress={sheetAddress}
        patientDob={patientDetails?.dob}
        patientGender={patientDetails?.gender}
        encounterId={encounterId}
        appointmentId={appointmentId}
      />
    </div>
  );
};

export default VisitOrdersPanel;
